import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useStateContext } from "../context/ContextProvider";
// import useFadeInOnScroll from "../components/Effects/FadeInOnScroll";
import { Suspense } from "react";

// Lazy load sections
const HeroSection = React.lazy(() => import("../components/HomeComponents/HeroSection"));
const EverythingSection = React.lazy(() => import("../components/HomeComponents/EverythingSection"));
const AboutSection = React.lazy(() => import("../components/HomeComponents/AboutSection"));
const ServicesSection = React.lazy(() => import("../components/HomeComponents/ServicesSection"));
const InsightsSection = React.lazy(() => import("../components/HomeComponents/InsightsSection"));
const TeamSection = React.lazy(() => import("../components/HomeComponents/TeamSection"));

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

function Home({ routes }) {
  const { isMobile, deviceSize } = useStateContext();
  const marker = false; // Debug markers off
  // useFadeInOnScroll();

  const homeSectionRef = useRef(null);
  const everythingRef = useRef(null);
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const caseRef = useRef(null);
  const insightsRef = useRef(null);
  const teamRef = useRef(null);

  const [visibleSections, setVisibleSections] = useState({
    hero: true, // Always load hero section initially
    everything: false,
    about: false,
    services: false,
    case: false,
    insights: false,
    team: false
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setVisibleSections((prev) => ({
              ...prev,
              [sectionId]: true,
            }));
          }
        });
      },
      { rootMargin: "200px", threshold: 0.1 } // Load 200px before entering viewport
    );

    [everythingRef, aboutRef, servicesRef, caseRef, insightsRef, teamRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={homeSectionRef} className="relative">
      {/* Hero Section (Always Loaded) */}
      <HeroSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />

      {/* Lazy-Loaded Sections */}
      <div id="everything" ref={everythingRef} className="w-[100vw] min-h-[200vh]">
        <Suspense fallback={<div></div>}>
          {visibleSections.everything && (
            <EverythingSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />
          )}
        </Suspense>
      </div>

      <div id="about" ref={aboutRef} className="w-[100vw] min-h-[50vh] lg:min-h-[100vh]">
        <Suspense fallback={<div></div>}>
          {visibleSections.about && (
            <AboutSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />
          )}
        </Suspense>
      </div>

      <div id="services" ref={servicesRef} className="w-[100vw] min-h-[140vh]">
        <Suspense fallback={<div></div>}>
          {visibleSections.services && (
            <ServicesSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />
          )}
        </Suspense>
      </div>

      <div id="insights" ref={insightsRef} className="relative w-[100vw] min-h-[50vh]" style={{ zIndex: 12 }}>
        <Suspense fallback={<div></div>}>
          {visibleSections.insights && (
            <InsightsSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />
          )}
        </Suspense>
      </div>

      <div id="team" ref={teamRef} className="relative w-[100vw] min-h-[50vh]" style={{ zIndex: 12 }}>
        <Suspense fallback={<div></div>}>
          {visibleSections.team && (
            <TeamSection isMobile={isMobile} deviceSize={deviceSize} marker={marker} />
          )}
        </Suspense>
      </div>
    </div>
  );
}

export default Home;